/**
 * Writing a guide, in either of two ways over one body.
 *
 * The handbook's markup is a small named vocabulary - a checklist, a warning, a
 * message template, a picture slot - and a stock WYSIWYG editor does not know
 * those classes: it strips them on the first save and turns a structured
 * procedure into flat prose. That is why this screen was HTML-only to begin
 * with, and it is still the reason the rich mode is not a stock editor: it runs
 * a document model built for exactly this vocabulary (`editor/nodes`), proven
 * against all twenty-two seeded guides in `editor/roundTrip.test.ts`.
 *
 * Both modes edit `draft.body`, the stored HTML, so switching between them is
 * free and neither is the "real" one. HTML stays because the seeds are written
 * that way, because a block the model does not know can still be pasted in, and
 * because somebody debugging an article wants to see the tags. The server
 * refuses markup outside the vocabulary either way, so a mistake is a message
 * rather than a broken page.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Code2, Eye, EyeOff, PenLine, Save } from "lucide-react";
import { cn, LoadingState } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { StaffUser } from "../../../lib/auth";
import {
  createGuide,
  getGuide,
  KB_CATEGORIES,
  updateGuide,
  type KbCategory,
  type KbDraft,
  type KbStatus,
} from "../../../lib/kb";
import { Btn, Card, CardHead, MenuField, useToast } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { Field, Pill, TagChips } from "../_shared/formBits";
import { KB_CATEGORY_META } from "./meta";
import { insertBlock, KB_SNIPPETS, splitBody } from "./lib";
import { KbRichEditor } from "./editor";

/** A new guide starts with the five headings, not with an empty box. */
const STARTER = [
  '<p class="kb-lede">Üks lause selle kohta, mida see juhend katab.</p>',
  "",
  '<h2 id="eesmark">1. Eesmärk ja standard</h2>',
  "<p>...</p>",
  "",
  '<h2 id="eeldused">2. Eeldused ja tööriistad</h2>',
  "<p>...</p>",
  "",
  '<h2 id="tegevused">3. Tegevusjuhend samm-sammult</h2>',
  "<h3>Samm 1. ...</h3>",
  '<ul class="kb-check">',
  "  <li>...</li>",
  "</ul>",
  "",
  '<h2 id="kvaliteet">4. Kvaliteet ja andmekvaliteet</h2>',
  "<p>...</p>",
  "",
  '<h2 id="erandid">5. Erandid ja pöördumised</h2>',
  '<table class="kb-table">',
  "  <thead>",
  "    <tr><th>Olukord</th><th>Mida teha</th><th>Kelle poole</th></tr>",
  "  </thead>",
  "  <tbody>",
  "    <tr><td>...</td><td>...</td><td>...</td></tr>",
  "  </tbody>",
  "</table>",
].join("\n");

const EMPTY: KbDraft = {
  slug: "",
  category: "process",
  title: "",
  summary: "",
  audience: "",
  tags: [],
  order: 500,
  status: "draft",
  body: STARTER,
};

/** Title to slug: lower case, Estonian letters folded, everything else a hyphen. */
function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[äã]/g, "a")
    .replace(/[öõ]/g, "o")
    .replace(/ü/g, "u")
    .replace(/š/g, "s")
    .replace(/ž/g, "z")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 100);
}

export function GuideEditor(_props: { user: StaffUser }) {
  const t = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const editing = Boolean(slug);

  const [id, setId] = useState<string | null>(null);
  const [draft, setDraft] = useState<KbDraft>(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(true);
  /** Which half of the body editor is on screen. The body is the same either way. */
  const [mode, setMode] = useState<"rich" | "html">("rich");
  const [problems, setProblems] = useState<string[]>([]);
  /** True once the title has been typed into a slug the operator did not set. */
  const [slugTouched, setSlugTouched] = useState(editing);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  /**
   * True once the caret has been inside the source box.
   *
   * A textarea nobody has clicked reports `selectionStart` 0, which reads
   * exactly like a caret parked deliberately at the top - so a block button
   * pressed before the box was ever touched would wedge a table above the lede.
   * Until it is touched, "here" means the end of the article.
   *
   * A boolean and the live DOM selection, rather than a remembered offset: a
   * textarea keeps its selection after focus moves to the button that was
   * clicked, in every browser, so the live value is both simpler and truer than
   * anything we could mirror into state.
   */
  const bodyTouched = useRef(false);
  /** Where the caret has to be put once React has drawn the inserted block.
   *  Null between insertions - a textarea whose selection is forced on every
   *  render would fight the operator for the cursor. */
  const [caret, setCaret] = useState<number | null>(null);

  useEffect(() => {
    if (!slug) return;
    getGuide(slug)
      .then((a) => {
        setId(a.id);
        setDraft({
          slug: a.slug,
          category: a.category as KbCategory,
          title: a.title,
          summary: a.summary,
          audience: a.audience,
          tags: a.tags,
          order: a.order,
          status: a.status,
          body: a.body,
        });
      })
      .catch(() => toast(t("common.error")))
      .finally(() => setLoading(false));
  }, [slug, t, toast]);

  const set = <K extends keyof KbDraft>(key: K, value: KbDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const previewParts = useMemo(() => splitBody(draft.body), [draft.body]);

  async function save() {
    setSaving(true);
    setProblems([]);
    try {
      const saved = id ? await updateGuide(id, draft) : await createGuide(draft);
      toast(t("kb.saved"));
      navigate(`/guides/${saved.slug}`);
    } catch (e) {
      const body = (e as { body?: { problems?: string[]; error?: string } }).body;
      if (body?.problems?.length) setProblems(body.problems);
      else if (body?.error === "slug_taken") setProblems([t("kb.slugTaken")]);
      else setProblems([t("common.error")]);
      setSaving(false);
    }
  }

  /**
   * Put the caret where the block just landed.
   *
   * After the state write, not during it: the textarea still holds the old
   * string when `insert` returns, and setting a selection past its length is
   * silently clamped to the end - which looks exactly like the append bug this
   * replaced.
   */
  useEffect(() => {
    if (caret == null) return;
    const el = bodyRef.current;
    setCaret(null);
    if (!el) return;
    bodyTouched.current = true;
    el.focus();
    el.setSelectionRange(caret, caret);
  }, [caret]);

  function insert(html: string) {
    const el = bodyRef.current;
    const end = draft.body.length;
    const live = el && bodyTouched.current;
    // Clamped: the DOM selection is a render behind if the body was just
    // retyped shorter, and an offset past the end would silently become one.
    const from = live ? Math.min(el.selectionStart, end) : end;
    const to = live ? Math.min(el.selectionEnd, end) : end;
    const next = insertBlock(draft.body, from, to, html);
    set("body", next.text);
    setCaret(next.caret);
  }

  if (loading) return <div className="crm-page"><LoadingState label={t("common.loading")} /></div>;

  return (
    // The shelf and status fields are `Pop` menus, and a `Pop` with no host
    // above it renders, takes the click and does nothing at all. Every page in
    // this app mounts its own; this one did not, so both dropdowns were dead.
    <PopHost>
      <div className="crm-page crm-kb-editor">
        <div className="crm-kb-crumbs">
          <button type="button" onClick={() => navigate(editing ? `/guides/${slug}` : "/guides")}>
            <ArrowLeft className="size-4" />
            {editing ? t("kb.backToArticle") : t("kb.title")}
          </button>
        </div>

        <Card>
          <CardHead
            title={editing ? t("kb.editTitle") : t("kb.newTitle")}
            sub={t("kb.editorHint")}
            actions={
              <>
                <Btn small onClick={() => setPreview(!preview)}>
                  {preview ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  {preview ? t("kb.hidePreview") : t("kb.showPreview")}
                </Btn>
                <Btn tone="dark" small onClick={() => void save()} disabled={saving || !draft.title.trim()}>
                  <Save className="size-4" />
                  {t("kb.save")}
                </Btn>
              </>
            }
          />

          {/* `crm-card` is a surface and nothing else - the head brings its own
              padding, the body does not - so without this wrapper every field,
              every block button and the source box ran flush to the card's edge
              while the title sat 26px in. */}
          <div className="crm-kb-body">
            <div className="crm-kb-fields">
              <div className="crm-kb-field">
                <Field label={t("kb.field.title")}>
                  <Pill
                    value={draft.title}
                    onChange={(v) => {
                      set("title", v);
                      if (!slugTouched) set("slug", slugify(v));
                    }}
                    placeholder={t("kb.field.titlePlaceholder")}
                  />
                </Field>
              </div>
              <div className="crm-kb-field">
                <Field label={t("kb.field.slug")} hint={`/guides/${draft.slug || "..."}`}>
                  <Pill
                    value={draft.slug}
                    onChange={(v) => {
                      setSlugTouched(true);
                      set("slug", v);
                    }}
                    placeholder="wordpress-igakuine-hooldus"
                  />
                </Field>
              </div>
              <div className="crm-kb-field">
                <Field label={t("kb.field.category")}>
                  <MenuField<KbCategory>
                    value={draft.category}
                    onChange={(v) => set("category", v)}
                    variant="form"
                    label={t("kb.field.category")}
                    options={KB_CATEGORIES.map((c) => ({ value: c, label: t(KB_CATEGORY_META[c].labelKey) }))}
                  />
                </Field>
              </div>
              <div className="crm-kb-field">
                <Field label={t("kb.field.status")}>
                  <MenuField<KbStatus>
                    value={draft.status}
                    onChange={(v) => set("status", v)}
                    variant="form"
                    label={t("kb.field.status")}
                    options={[
                      { value: "published", label: t("kb.published") },
                      { value: "draft", label: t("kb.draft") },
                    ]}
                  />
                </Field>
              </div>
              <div className="crm-kb-field">
                <Field label={t("kb.field.audience")}>
                  <Pill value={draft.audience} onChange={(v) => set("audience", v)} placeholder={t("kb.field.audiencePlaceholder")} />
                </Field>
              </div>
              <div className="crm-kb-field">
                <Field label={t("kb.field.order")}>
                  <Pill
                    value={String(draft.order)}
                    onChange={(v) => set("order", Number(v.replace(/\D/g, "")) || 1)}
                    inputMode="numeric"
                  />
                </Field>
              </div>
              <div className="crm-kb-field is-wide">
                <Field label={t("kb.field.summary")}>
                  <textarea
                    className="crm-area"
                    value={draft.summary}
                    onChange={(e) => set("summary", e.target.value.slice(0, 300))}
                    rows={2}
                    placeholder={t("kb.field.summaryPlaceholder")}
                  />
                </Field>
              </div>
              <div className="crm-kb-field">
                <Field label={t("kb.field.tags")}>
                  <TagChips
                    tags={draft.tags}
                    onChange={(next) => set("tags", next.map((x) => x.toLowerCase()))}
                    placeholder={t("kb.field.tagsPlaceholder")}
                    maxLen={40}
                    maxCount={20}
                    removeLabel={t("common.remove")}
                  />
                </Field>
              </div>
            </div>

            {problems.length ? (
              <div className="crm-kb-problems">
                <strong>{t("kb.problems")}</strong>
                <ul>
                  {problems.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="crm-kb-modes">
              <div className="crm-kb-modeset" role="tablist" aria-label={t("kb.modeHint")}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "rich"}
                  className={cn("crm-kb-mode", mode === "rich" && "is-on")}
                  onClick={() => setMode("rich")}
                >
                  <PenLine className="size-4" />
                  {t("kb.mode.rich")}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === "html"}
                  className={cn("crm-kb-mode", mode === "html" && "is-on")}
                  onClick={() => setMode("html")}
                >
                  <Code2 className="size-4" />
                  {t("kb.mode.html")}
                </button>
              </div>
              {mode === "html" ? (
                <div className="crm-kb-snippets">
                  <span>{t("kb.insert")}</span>
                  {KB_SNIPPETS.map((s) => (
                    <Btn key={s.key} small onClick={() => insert(s.html)}>
                      {t(`kb.snippet.${s.key}`)}
                    </Btn>
                  ))}
                </div>
              ) : null}
            </div>

            <div className={cn("crm-kb-split", preview && "is-split")}>
              {mode === "rich" ? (
                <KbRichEditor value={draft.body} onChange={(html) => set("body", html)} />
              ) : (
                <textarea
                  ref={bodyRef}
                  className="crm-area crm-kb-source"
                  value={draft.body}
                  onChange={(e) => set("body", e.target.value)}
                  onFocus={() => {
                    bodyTouched.current = true;
                  }}
                  spellCheck={false}
                  rows={30}
                />
              )}

              {preview ? (
                <div className="crm-doc crm-kb-preview">
                  {previewParts.map((seg, i) =>
                    seg.kind === "html" ? (
                      <div key={i} dangerouslySetInnerHTML={{ __html: seg.html }} />
                    ) : (
                      <figure key={`${seg.key}-${i}`} className="crm-doc-slot is-preview">
                        <figcaption>
                          <span className="crm-doc-slot-kicker">{t("kb.shot.missing")}</span>
                          <span className="crm-doc-slot-cap">{seg.caption}</span>
                        </figcaption>
                      </figure>
                    ),
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </Card>
      </div>
    </PopHost>
  );
}
